<template>
  <div
    class="VisionAccordion"
    :class="[
      props.displayAsBlockAtBreakpoint
        ? 'VisionAccordion--display-as-block-at-breakpoint-' +
          props.displayAsBlockAtBreakpoint
        : '',
      {
        'VisionAccordion--is-expanded': isExpanded,
        'VisionAccordion--no-border': noBorder,
      },
    ]"
  >
    <label class="VisionAccordion__title">
      <div>
        <slot name="header" />
      </div>
      <VisionIcon
        class="VisionAccordion__icon"
        :rotate="0"
        icon="plus"
        colour="black"
        size="x-small"
      />
      <input
        class="VisionAccordion__input"
        type="checkbox"
        :checked="isExpanded"
        @change="handleCheckBoxChange"
      />
    </label>
    <div
      ref="accordionContent"
      class="VisionAccordion__container"
    >
      <div class="VisionAccordion__content">
        <slot name="body" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import VisionIcon from '../VisionIcon/VisionIcon.vue'
import { screenSizes } from '../../tailwind/storefront/base.js'

interface VisionAccordionProps {
  displayAsBlockAtBreakpoint?:
    | ''
    | 'xxs'
    | 'xs'
    | 'md'
    | 'lg'
    | 'xl'
    | 'xxl'
    | 'full'
  defaultExpanded?: boolean
  noBorder?: boolean
}

const props = withDefaults(defineProps<VisionAccordionProps>(), {
  displayAsBlockAtBreakpoint: '',
  defaultExpanded: false,
  noBorder: false
})

const accordionContent = ref()
const isExpanded = ref(props.defaultExpanded)

function handleCheckBoxChange(value) {
  if (
    props.displayAsBlockAtBreakpoint &&
    window.matchMedia(
      `(min-width: ${screenSizes[props.displayAsBlockAtBreakpoint]}px)`,
    ).matches
  ) {
    return
  }

  isExpanded.value = true

  accordionContent.value.addEventListener(
    'transitionend',
    () => {
      if (value.target.checked) {
        afterEnter(accordionContent.value)
      } else if (!value.target.checked) {
        afterLeave(accordionContent.value)
      }
    },
    { once: true },
  )

  if (value.target.checked) {
    enter(accordionContent.value)
  } else if (!value.target.checked) {
    leave(accordionContent.value)
  }
}

// Mimics SlideDownTransition from Storefront
function enter(element) {
  const width = getComputedStyle(element).width

  element.style.width = width
  element.style.position = 'absolute'
  element.style.visibility = 'hidden'
  element.style.height = 'auto'

  const height = getComputedStyle(element).height

  element.style.width = null
  element.style.position = null
  element.style.visibility = null
  element.style.height = 0
  getComputedStyle(element).height

  requestAnimationFrame(() => {
    element.style.height = height
  })
}

function afterEnter(element: HTMLElement) {
  element.style.height = 'auto'
}

function leave(element: HTMLElement) {
  const height = getComputedStyle(element).height

  element.style.height = height
  element.style.display = 'flex'

  getComputedStyle(element).height

  requestAnimationFrame(() => {
    element.style.height = '0'
  })
}

function afterLeave(element: HTMLElement) {
  isExpanded.value = false

  element.style.height = 'auto'
  element.style.display = ''
}
</script>

<style lang="postcss">
.VisionAccordion {
  @apply border-b flex flex-col;
  border-color: var(--vision-accordion-border-color, theme('colors.grey.200'));
}

.VisionAccordion__title {
  @apply relative flex items-center justify-between gap-4
    py-6 md:py-8
    w-full font-light;
}

.VisionAccordion__input {
  @apply hidden;
}

.VisionAccordion__title:has(input:checked) {
  & + .VisionAccordion__container {
    @apply flex;

    .VisionAccordion__content {
      @apply opacity-100 translate-y-0;
    }
  }

  .VisionAccordion__icon {
    @apply rotate-[45deg];
  }
}

.VisionAccordion__icon {
  @apply transition-transform duration-300 ease-in-out;
}

.VisionAccordion__container {
  @apply hidden overflow-hidden w-auto relative;
  transition: all 0.48s ease-in-out;
}

.VisionAccordion__content {
  @apply relative pt-0 w-full -translate-y-4 opacity-0
  transition-all duration-[480ms];
}

.VisionAccordion--no-border {
  @apply border-0;
}

.VisionAccordion--default-expanded {
  .VisionAccordion__container {
    @apply flex;

    .VisionAccordion__content {
      @apply opacity-100 translate-y-0;
    }
  }

  .VisionAccordion__icon {
    @apply rotate-[45deg];
  }
}

.VisionAccordion--is-expanded {
  .VisionAccordion__container {
    @apply flex;
  }

  .VisionAccordion__content {
    @apply opacity-100 translate-y-0;
  }
}

.VisionAccordion--display-as-block-at-breakpoint-xxs {
  @screen xxs {
    @apply border-b-0;

    .VisionAccordion__title {
      .VisionAccordion__icon {
        @apply hidden;
      }
    }

    .VisionAccordion__container {
      @apply flex;

      .VisionAccordion__content {
        @apply opacity-100 translate-y-0;
      }
    }
  }
}

.VisionAccordion--display-as-block-at-breakpoint-xs {
  @screen xs {
    @apply border-b-0;

    .VisionAccordion__title {
      .VisionAccordion__icon {
        @apply hidden;
      }
    }

    .VisionAccordion__container {
      @apply flex;

      .VisionAccordion__content {
        @apply opacity-100 translate-y-0;
      }
    }
  }
}

.VisionAccordion--display-as-block-at-breakpoint-md {
  @screen md {
    @apply border-b-0;

    .VisionAccordion__title {
      .VisionAccordion__icon {
        @apply hidden;
      }
    }

    .VisionAccordion__container {
      @apply flex;

      .VisionAccordion__content {
        @apply opacity-100 translate-y-0;
      }
    }
  }
}

.VisionAccordion--display-as-block-at-breakpoint-lg {
  @screen lg {
    @apply border-b-0;

    .VisionAccordion__title {
      .VisionAccordion__icon {
        @apply hidden;
      }
    }

    .VisionAccordion__container {
      @apply flex;

      .VisionAccordion__content {
        @apply opacity-100 translate-y-0;
      }
    }
  }
}

.VisionAccordion--display-as-block-at-breakpoint-xl {
  @screen xl {
    @apply border-b-0;

    .VisionAccordion__title {
      .VisionAccordion__icon {
        @apply hidden;
      }
    }

    .VisionAccordion__container {
      @apply flex;

      .VisionAccordion__content {
        @apply opacity-100 translate-y-0;
      }
    }
  }
}

.VisionAccordion--display-as-block-at-breakpoint-xxl {
  @screen xxl {
    @apply border-b-0;

    .VisionAccordion__title {
      .VisionAccordion__icon {
        @apply hidden;
      }
    }

    .VisionAccordion__container {
      @apply flex;

      .VisionAccordion__content {
        @apply opacity-100 translate-y-0;
      }
    }
  }
}

.VisionAccordion--display-as-block-at-breakpoint-full {
  @screen full {
    @apply border-b-0;

    .VisionAccordion__title {
      .VisionAccordion__icon {
        @apply hidden;
      }
    }

    .VisionAccordion__container {
      @apply flex;

      .VisionAccordion__content {
        @apply opacity-100 translate-y-0;
      }
    }
  }
}
</style>
